import React from 'react';
import { render, act, fireEvent } from '@testing-library/react';

// QA validation suite for #253 (display-level live-stream override).
//
// Scope: covers gaps the regression suite in
// `playlist_slideshow.test.jsx` does NOT exercise — full data-attr
// round-trip, successive went_live replacement semantics, both
// directions of state-recovery (live-while-not-in-override and
// not-live-while-in-override), fetch failure handling, and the
// behaviour of `billboardOutputId` changing after mount.
//
// Uses the same Pusher mock pattern as the regression file so each
// test can drive event handlers and inspect bindings.
jest.mock('pusher-js', () => {
    const instances = [];
    const ctor = jest.fn().mockImplementation((key, opts) => {
        const channelBindings = {};
        const connectionBindings = {};
        const channel = {
            bind: jest.fn((eventName, handler) => {
                channelBindings[eventName] = handler;
            }),
            unbind: jest.fn(),
        };
        const instance = {
            key,
            opts,
            channel,
            channelBindings,
            connectionBindings,
            subscribedChannelName: null,
            subscribe: jest.fn((name) => {
                instance.subscribedChannelName = name;
                return channel;
            }),
            unsubscribe: jest.fn(),
            disconnect: jest.fn(),
            connection: {
                bind: jest.fn((eventName, handler) => {
                    connectionBindings[eventName] = handler;
                }),
                unbind: jest.fn(),
            },
        };
        instances.push(instance);
        return instance;
    });
    ctor.__instances = instances;
    ctor.__reset = () => { ctor.mockClear(); instances.length = 0; };
    return ctor;
});

import Pusher from 'pusher-js';
import PlaylistSlideshow from './playlist_slideshow';

// Fullscreen API shim — same as regression file.
document.documentElement.requestFullscreen = jest.fn().mockResolvedValue();
document.exitFullscreen = jest.fn().mockResolvedValue();
Object.defineProperty(document, 'fullscreenElement', { value: null, writable: true });

const mockItems = [
    { billboard_media_item: { url: 'https://example.com/1.jpg' } },
    { billboard_media_item: { url: 'https://example.com/2.jpg' } },
    { billboard_media_item: { url: 'https://example.com/3.jpg' } },
];

describe('PlaylistSlideshow #253 QA — live stream override edge cases', () => {
    beforeEach(() => {
        jest.useFakeTimers();
        Pusher.__reset();
        // Default fetch shim — individual tests override per-call.
        global.fetch = jest.fn().mockResolvedValue({
            ok: true,
            json: () => Promise.resolve({ live: false }),
        });
    });

    afterEach(() => {
        jest.useRealTimers();
        delete global.fetch;
    });

    // ------------------------------------------------------------------
    // 1. Override placeholder data-attrs round-trip — every field of the
    //    payload must appear on the overlay so #254 can hydrate the iframe
    //    purely from DOM state.
    // ------------------------------------------------------------------
    it('round-trips every went_live payload field onto data-* attributes', () => {
        const { container } = render(
            <PlaylistSlideshow
                items={mockItems}
                onClose={jest.fn()}
                billboardOutputId={42}
            />
        );
        const instance = Pusher.__instances[0];

        act(() => {
            instance.channelBindings['live_stream_source.went_live']({
                source_id: 99,
                billboard_output_id: 42,
                channel_id: 'UCexamplechannelid12345',
                video_id: 'abc123XYZ_-',
                provider: 'youtube_live',
            });
        });

        const overlay = container.querySelector('[data-live-stream-override="true"]');
        expect(overlay).not.toBeNull();
        expect(overlay.getAttribute('data-source-id')).toBe('99');
        expect(overlay.getAttribute('data-video-id')).toBe('abc123XYZ_-');
        expect(overlay.getAttribute('data-channel-id')).toBe('UCexamplechannelid12345');
        expect(overlay.getAttribute('data-provider')).toBe('youtube_live');
    });

    // ------------------------------------------------------------------
    // 2. Successive went_live with different sourceId — the second event
    //    must fully replace the first event's payload on the placeholder.
    //    This pins the "swap on the fly" semantic for the case where a
    //    second source goes live before the first ends (worker bug, or
    //    an admin reconfigures while a stream is on air).
    // ------------------------------------------------------------------
    it('replaces placeholder payload when a second went_live fires before ended', () => {
        const { container } = render(
            <PlaylistSlideshow
                items={mockItems}
                onClose={jest.fn()}
                billboardOutputId={42}
            />
        );
        const instance = Pusher.__instances[0];

        act(() => {
            instance.channelBindings['live_stream_source.went_live']({
                source_id: 1,
                billboard_output_id: 42,
                channel_id: 'UCfirstchannelid1234567',
                video_id: 'firstVid___',
                provider: 'youtube_live',
            });
        });

        let overlay = container.querySelector('[data-live-stream-override="true"]');
        expect(overlay.getAttribute('data-source-id')).toBe('1');
        expect(overlay.getAttribute('data-video-id')).toBe('firstVid___');

        act(() => {
            instance.channelBindings['live_stream_source.went_live']({
                source_id: 2,
                billboard_output_id: 42,
                channel_id: 'UCsecondchannelid123456',
                video_id: 'secondVid__',
                provider: 'youtube_live',
            });
        });

        overlay = container.querySelector('[data-live-stream-override="true"]');
        expect(overlay.getAttribute('data-source-id')).toBe('2');
        expect(overlay.getAttribute('data-video-id')).toBe('secondVid__');
        expect(overlay.getAttribute('data-channel-id')).toBe('UCsecondchannelid123456');
        // No stale fragment of the first payload survives.
        expect(overlay.getAttribute('data-video-id')).not.toBe('firstVid___');
    });

    // ------------------------------------------------------------------
    // 3. State-recovery returns {live: true} while NOT currently in
    //    override — happens when the Pusher socket dropped just before
    //    (or during) the went_live broadcast. The reconnect handler is
    //    the safety net.
    // ------------------------------------------------------------------
    it('enters override on reconnect when state-recovery reports live=true', async () => {
        const { container } = render(
            <PlaylistSlideshow
                items={mockItems}
                onClose={jest.fn()}
                billboardOutputId={42}
            />
        );
        const instance = Pusher.__instances[0];

        // Currently NOT in override — confirm baseline.
        expect(container.querySelector('img')).not.toBeNull();

        global.fetch.mockClear();
        global.fetch.mockResolvedValueOnce({
            ok: true,
            json: () => Promise.resolve({
                live: true,
                source_id: 5,
                billboard_output_id: 42,
                channel_id: 'UCrecoveredchannelidabc',
                video_id: 'recoveredVid',
                provider: 'youtube_live',
            }),
        });

        await act(async () => {
            instance.connectionBindings['connected']();
            await Promise.resolve();
            await Promise.resolve();
        });

        // YouTube iframe mounted from recovered payload (#254)
        const iframe = container.querySelector('iframe');
        expect(iframe).not.toBeNull();
        expect(iframe.src).toContain('youtube.com/embed/recoveredVid');
        const overlay = container.querySelector('[data-live-stream-override="true"]');
        expect(overlay.getAttribute('data-source-id')).toBe('5');
        expect(overlay.getAttribute('data-video-id')).toBe('recoveredVid');
        expect(overlay.getAttribute('data-channel-id')).toBe('UCrecoveredchannelidabc');
    });

    // ------------------------------------------------------------------
    // 4. State-recovery returns {live: false} while IN override — the
    //    `ended` event fired while we were disconnected. The recovery
    //    fetch is the corrective channel.
    // ------------------------------------------------------------------
    it('exits override on reconnect when state-recovery reports live=false (via the #254 fade)', async () => {
        const { container } = render(
            <PlaylistSlideshow
                items={mockItems}
                onClose={jest.fn()}
                billboardOutputId={42}
            />
        );
        const instance = Pusher.__instances[0];

        // Force into override first.
        act(() => {
            instance.channelBindings['live_stream_source.went_live']({
                source_id: 7,
                billboard_output_id: 42,
                channel_id: 'UCxxx',
                video_id: 'liveVid',
                provider: 'youtube_live',
            });
        });
        expect(container.querySelector('[data-live-stream-override="true"]')).not.toBeNull();

        global.fetch.mockClear();
        global.fetch.mockResolvedValueOnce({
            ok: true,
            json: () => Promise.resolve({ live: false }),
        });

        await act(async () => {
            instance.connectionBindings['connected']();
            await Promise.resolve();
            await Promise.resolve();
        });

        // #254: state-recovery {live:false} routes through handleEnded, which
        // begins the 1s fade. Overlay still mounted, opacity 0.
        const fading = container.querySelector('[data-live-stream-override="true"]');
        expect(fading).not.toBeNull();
        expect(fading.style.opacity).toBe('0');

        // Drive the transitionend to complete phase 2.
        act(() => {
            fireEvent.transitionEnd(fading, { propertyName: 'opacity' });
        });

        // Override cleared — playlist is back, on slide 1 (index 0 reset).
        expect(container.querySelector('[data-live-stream-override="true"]')).toBeNull();
        expect(container.querySelector('img').src).toBe('https://example.com/1.jpg');
    });

    // ------------------------------------------------------------------
    // 5. State-recovery fetch fails (5xx / network error). Player must
    //    leave existing state alone — no spurious toggles, no crash.
    // ------------------------------------------------------------------
    it('keeps current state when state-recovery fetch errors out', async () => {
        const { container } = render(
            <PlaylistSlideshow
                items={mockItems}
                onClose={jest.fn()}
                billboardOutputId={42}
            />
        );
        const instance = Pusher.__instances[0];

        // Drop into override so we can prove the fetch error does NOT
        // accidentally clear it.
        act(() => {
            instance.channelBindings['live_stream_source.went_live']({
                source_id: 11,
                billboard_output_id: 42,
                channel_id: 'UCstayingput12345678901',
                video_id: 'stickyVid__',
                provider: 'youtube_live',
            });
        });
        expect(container.querySelector('[data-live-stream-override="true"]')).not.toBeNull();

        // Reconnect → 5xx. Handler should catch and leave state alone.
        global.fetch.mockClear();
        global.fetch.mockResolvedValueOnce({
            ok: false,
            status: 500,
            json: () => Promise.resolve({ error: 'boom' }),
        });

        await act(async () => {
            // No throw, no unhandled-promise scream.
            instance.connectionBindings['connected']();
            await Promise.resolve();
            await Promise.resolve();
        });

        // Still in override with the same payload.
        const overlay = container.querySelector('[data-live-stream-override="true"]');
        expect(overlay).not.toBeNull();
        expect(overlay.getAttribute('data-source-id')).toBe('11');
        expect(overlay.getAttribute('data-video-id')).toBe('stickyVid__');

        // And a network-rejection (not just !ok) ALSO must not break things.
        global.fetch.mockClear();
        global.fetch.mockRejectedValueOnce(new Error('network down'));
        await act(async () => {
            instance.connectionBindings['connected']();
            await Promise.resolve();
            await Promise.resolve();
        });
        // Override still present — no spurious clear.
        expect(container.querySelector('[data-live-stream-override="true"]')).not.toBeNull();
    });

    // ------------------------------------------------------------------
    // 6. billboardOutputId changes between renders.
    //
    //    componentDidUpdate re-subscribes when the prop changes so a
    //    route swap reusing the same component instance doesn't leave
    //    the player bound to the previous channel.
    // ------------------------------------------------------------------
    it('re-subscribes to the new channel when billboardOutputId changes between renders', () => {
        const { rerender } = render(
            <PlaylistSlideshow
                items={mockItems}
                onClose={jest.fn()}
                billboardOutputId={42}
            />
        );
        expect(Pusher).toHaveBeenCalledTimes(1);
        const firstInstance = Pusher.__instances[0];
        expect(firstInstance.subscribedChannelName).toBe('billboard-output-42');

        rerender(
            <PlaylistSlideshow
                items={mockItems}
                onClose={jest.fn()}
                billboardOutputId={43}
            />
        );

        // Old subscription torn down, new Pusher + new subscribe fired.
        expect(firstInstance.unsubscribe).toHaveBeenCalledWith('billboard-output-42');
        expect(Pusher).toHaveBeenCalledTimes(2);
        const secondInstance = Pusher.__instances[1];
        expect(secondInstance.subscribedChannelName).toBe('billboard-output-43');
    });
});
